import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import DashboardNav from '../components/DashboardNav';
import { MobileDashboardHeader, MobileNavDrawer } from '../components/MobileDashboardChrome';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { btnPrimary, btnPrimarySmall, btnGhost, tabNavContainer, tabNavItemColored, introCardKeyline, introHeadlineSerifDeep, linkInline, linkInlineMuted, linkMessage, DASHBOARD_NAV_WIDTH_PX, layoutMobileMediaQuery, demoSectionCardStackStyle, DEMO_MINI_CARD_GRID_GAP_PX, DEMO_PRODUCT_INPUT_SCOPE_CLASS } from '../designSystem';
import { NOVA_VOICE } from '../data/novaCommunityConfig';
import { FIRST_CHAPTER_FEED_POSTS, SARAH_THREAD, CLINICAL_NOTE_THREAD, TAKING_A_BREATH_THREAD } from '../data/communityDemoThreads';
import { CommunityFeed } from '../components/CommunityFeed';
import { LiveRoomsPage } from '../components/LiveRoomsPage';
import { LIVE_CHAT_ROOMS } from '../data';
import { CHAPTER_THEMES } from '../data/chapterThemes';
import NovaAvatarImg, { novaAvatarChapterFromSlug } from '../components/NovaAvatarImg';

const FONT_BODY = "'Plus Jakarta Sans', -apple-system, sans-serif";

export const TABS = [
  { key: 'feed', label: 'My Chapter' },
  { key: 'live', label: 'Live rooms' },
  { key: 'groups', label: 'Groups' },
  { key: 'friends', label: 'Friends' },
  { key: 'myposts', label: 'My posts' },
];

const YOUR_GROUPS = [
  { name: 'IVF Warriors — Cycle 1', members: '3,240', bg: '#FBEAF0' },
  { name: 'Due in Summer 2026', members: '1,890', bg: '#E1F5EE' },
];

const SUGGESTED_GROUPS = [
  { name: 'PCOS Sisters', members: '4,100', bg: '#EEEDFE', matched: true },
  { name: 'Low AMH Warriors', members: '2,100', bg: '#FBEAF0', matched: true },
  { name: 'Fertility & Mental Health', members: '1,890', bg: '#FEF0EB', matched: false },
];

const FRIENDS_ONLINE = [
  { name: 'Jasmine M.', slug: 'jasmine', desc: 'Cycle 2 · stim day 8 like you', bg: '#FBEAF0', color: '#993556' },
  { name: 'Ava K.', slug: 'ava', desc: 'Transfer prep · Cincinnati', bg: '#E6F1FB', color: '#185FA5' },
];

const FRIENDS_RECOMMEND = [
  { name: 'Noelle R.', slug: 'noelle', desc: 'Day 8 · Cleveland · antagonist', bg: '#FBEAF0', color: '#993556' },
  { name: 'Emma T.', slug: 'emma', desc: 'IVF cycle 2 · week 18', bg: '#EEEDFE', color: '#534AB7' },
  { name: 'Sofia L.', slug: 'sofia', desc: 'Postpartum · low AMH', bg: '#E1F5EE', color: '#0F6E56' },
];

const LIVE_NOW = [
  { name: 'Stims support', count: 7, orange: false },
  { name: 'Transfer & TWW', count: 9, orange: false },
  { name: 'Retrieval recovery', count: 6, orange: false },
  { name: 'Beta day', count: 3, orange: true },
];

/** First Chapter / teen demo — community sidebar + live strip */
const FC_LIVE_NOW = [
  { name: 'Cycle tracking beginners', count: 9, orange: false },
  { name: 'Cramps & bad period days', count: 11, orange: false },
  { name: 'Mood, PMS & big feelings', count: 7, orange: false },
  { name: 'Talk to parent / school nurse', count: 6, orange: true },
];

const FC_NOVA_GROUPS = [
  { name: 'Teens & Periods', meta: '7,400 members · no dumb questions', joined: true, bg: '#EEEDFE' },
  { name: 'Cycle tracking beginners', meta: '2,100 · Nova matched you', joined: false, bg: '#E8E0D8' },
  { name: 'Mood & mental health', meta: '2,800 members · hormones & feels', joined: false, bg: '#E1F5EE' },
  { name: 'Cramps comfort crew', meta: '4,100 · what actually helps', joined: false, bg: '#EEEDFE' },
];

const FC_FRIENDS_ONLINE = [
  { name: 'Emma T.', slug: 'emma', desc: 'Cycle day 14 · learning her cycle · Portland', bg: '#EEEDFE', color: '#534AB7' },
  { name: 'Lia K.', slug: 'lia', desc: '28-day cycle · mood tracking · Austin', bg: '#E1F5EE', color: '#085041' },
];

const FC_FRIENDS_RECOMMEND = [
  { name: 'Aisha R.', slug: 'aisha', desc: 'First period last year · Columbus, OH', bg: '#EEEDFE', color: '#534AB7' },
  { name: 'Sofia M.', slug: 'sofia', desc: 'PMS rage is real · Denver', bg: '#FBEAF0', color: '#993556' },
  { name: 'Nina P.', slug: 'nina', desc: 'School nurse helped me talk to my mom · Chicago', bg: '#E6F1FB', color: '#185FA5' },
];

const FC_NOVA_ASK_CHIPS = [
  'Is my period flow normal for my age?',
  'How do I ask for pads at school without dying?',
  'Why am I so angry the week before my period?',
];

const FC_YOUR_GROUPS = [
  { name: 'Teens & Periods — First Chapter', members: '7,400', bg: '#EEEDFE' },
  { name: 'Mood & Mental Health', members: '2,800', bg: '#E1F5EE' },
];

const FC_SUGGESTED_GROUPS = [
  { name: 'Cycle tracking beginners', members: '2,100', bg: '#EEEDFE', matched: true },
  { name: 'Cramps comfort crew', members: '4,100', bg: '#FEF0EB', matched: true },
  { name: 'Talk to my parent scripts', members: '1,600', bg: '#E1F5EE', matched: false },
];

function initials(str) {
  const s = String(str || '').trim();
  if (!s) return '?';
  return s.split(/\s+/).map((p) => p[0]).join('').toUpperCase().slice(0, 2);
}

function hexWithAlpha(hex, alpha) {
  if (!hex || !String(hex).startsWith('#')) return hex;
  const a = Math.max(0, Math.min(1, alpha));
  const hexA = Math.round(a * 255).toString(16).padStart(2, '0');
  return `${hex}${hexA}`; // React supports 8-digit hex in inline styles
}

const COMMUNITY_META_BY_KEY = {
  fertility: { label: 'Fertility', emoji: '🌸' },
  ttc: { label: 'TTC', emoji: '💜' },
  pregnancy: { label: 'Pregnancy', emoji: '🤰' },
  motherhood: { label: 'Motherhood', emoji: '🤱' },
  postpartum: { label: 'Motherhood', emoji: '🤱' },
  cycle: { label: 'Trying to Conceive', emoji: '💜' },
  'first-chapter': { label: 'Trying to Conceive', emoji: '💜' },
  menopause: { label: 'Motherhood', emoji: '🤱' },
};

function metaForChapterKey(chapterKey) {
  return COMMUNITY_META_BY_KEY[chapterKey] || COMMUNITY_META_BY_KEY.fertility;
}

export default function Community() {
  const navigate = useNavigate();
  const location = useLocation();
  const m = useMediaQuery(layoutMobileMediaQuery);
  const [navOpen, setNavOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('feed');
  const [novaOpen, setNovaOpen] = useState(true);
  const [feedSort, setFeedSort] = useState('recent');
  const [askInputFocused, setAskInputFocused] = useState(false);

  const isDemo = location.pathname.startsWith('/demo/');
  const demoChapterRaw = isDemo ? (location.pathname.match(/^\/demo\/([^/]+)/)?.[1] || 'fertility') : 'fertility';
  const demoProductInputScope =
    isDemo && ['fertility', 'ttc', 'trying-to-conceive'].includes(demoChapterRaw);
  const chapterKey = demoChapterRaw;
  const theme = CHAPTER_THEMES[chapterKey] || CHAPTER_THEMES.fertility;
  const accent = theme.accent;
  const accentBg = theme.accentBg;
  const chapterMeta = metaForChapterKey(chapterKey);
  const novaCommunityTitle = NOVA_VOICE[chapterKey]?.communityTitle || NOVA_VOICE.fertility.communityTitle;
  const profileBasePath = isDemo ? `/demo/${demoChapterRaw}/profile` : '/profile';
  const connectPath = isDemo ? `/demo/${demoChapterRaw}/connect` : '/connect';
  const isFirstChapter = chapterKey === 'cycle' || chapterKey === 'first-chapter';
  const novaAvatarChapter = novaAvatarChapterFromSlug(demoChapterRaw);
  const accentText = theme.accentText || accent;
  const introGradientEnd = isFirstChapter ? '#f0eefc' : '#f5f0f5';

  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  return (
    <>
    <div style={{
      display: 'grid',
      minHeight: '100vh',
      height: m ? 'auto' : '100vh',
      background: 'var(--sand)',
      fontFamily: FONT_BODY,
      gridTemplateColumns: m ? 'minmax(0,1fr)' : `${DASHBOARD_NAV_WIDTH_PX}px minmax(0,1fr)`,
      gridTemplateRows: m ? 'auto minmax(0, 1fr)' : 'minmax(0, 1fr)',
    }}>
      {!m && <DashboardNav />}
      {m && <MobileDashboardHeader onMenu={() => setNavOpen(true)} />}

      <div style={{ display: 'flex', flex: 1, minWidth: 0, minHeight: 0, height: m ? 'auto' : '100%', overflow: 'visible', flexDirection: m ? 'column' : 'row' }}>
        {/* Left column */}
        <div
          className={demoProductInputScope ? DEMO_PRODUCT_INPUT_SCOPE_CLASS : undefined}
          style={{ flex: 1, minWidth: 0, overflow: 'auto', padding: '16px 24px 20px' }}
        >
          {(demoChapterRaw === 'ttc' || demoChapterRaw === 'trying-to-conceive') ? (
            <div className="page-header-block chapter-ttc" style={{ marginBottom: 12 }}>
              <div className="page-eyebrow">💜 TTC · Community</div>
              <div className="page-title-card" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                <div>
                  <h1 className="page-title">Your TTC community</h1>
                  <p className="page-descriptor">Matched to your cycle day and situation.</p>
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0 }}>
                  <span className="page-badge-ttc">Cycle 4 · Day 14</span>
                  <button className="page-cta-ttc" type="button" onClick={() => navigate('/signup')}>Join the waitlist</button>
                </div>
              </div>
              <div className="page-pills-ttc">
                <span className="pill-ttc">
                  <span className="pill-dot-ttc" />
                  Fertile window · 12 online
                </span>
                <span className="pill-ttc">OPK reading room live</span>
                <span className="pill-ttc">Matched · Cycle 4 · Columbus</span>
              </div>
            </div>
          ) : (
            <div
              style={{
                background: `linear-gradient(140deg, ${accentBg}, ${introGradientEnd})`,
                ...introCardKeyline,
                padding: '22px 26px',
                marginBottom: 12,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                <div>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 500,
                      letterSpacing: '0.1em',
                      textTransform: 'uppercase',
                      color: accent,
                      marginBottom: 4,
                    }}
                  >
                    Community · {chapterMeta.label}
                  </div>
                  <div style={{ ...introHeadlineSerifDeep, fontSize: 16, marginBottom: 3 }}>
                    Your {chapterMeta.label === 'TTC' ? 'TTC' : chapterMeta.label.toLowerCase()} community
                  </div>
                  <div style={{ fontSize: 12, color: 'rgba(60,30,45,0.5)', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span>{chapterMeta.emoji} {chapterMeta.label} chapter — everyone here can see your posts.</span>
                    <span style={{ color: 'rgba(60,30,45,0.4)' }}>·</span>
                    <span>12,450 members</span>
                    <span style={{ color: 'rgba(60,30,45,0.4)' }}>·</span>
                    <span style={{ color: '#1D9E75', fontWeight: 500 }}>142 online now</span>
                  </div>
                </div>
                <button type="button" onClick={() => navigate('/signup')} style={{ ...btnPrimary, background: accent, flexShrink: 0 }}>+ New post</button>
              </div>
            </div>
          )}

          {/* 2. Tab bar — TYPE 3 */}
          <div style={{ ...tabNavContainer, marginBottom: 14, overflowX: 'auto' }}>
            {TABS.map((t) => (
              <button key={t.key} type="button" onClick={() => setActiveTab(t.key)} style={tabNavItemColored(activeTab === t.key, accent)}>
                {t.label}
              </button>
            ))}
          </div>

          {/* 3. Tab content */}
          {activeTab === 'feed' && (
            <FeedTab
              navigate={navigate}
              m={m}
              feedSort={feedSort}
              setFeedSort={setFeedSort}
              isDemo={isDemo}
              accent={accent}
              accentBg={accentBg}
              chapterKey={chapterKey}
              chapterMeta={chapterMeta}
              demoChapterRaw={demoChapterRaw}
            />
          )}
          {activeTab === 'live' && (
            <LiveTab navigate={navigate} chapterKey={chapterKey} />
          )}
          {activeTab === 'groups' && (
            <GroupsTab navigate={navigate} chapterMeta={chapterMeta} accent={accent} accentBg={accentBg} chapterKey={chapterKey} />
          )}
          {activeTab === 'friends' && (
            <FriendsTab navigate={navigate} profileBasePath={profileBasePath} connectPath={connectPath} accent={accent} chapterKey={chapterKey} />
          )}
          {activeTab === 'myposts' && (
            <MyPostsTab navigate={navigate} profileBasePath={profileBasePath} connectPath={connectPath} accent={accent} />
          )}
        </div>

        {/* Right: Nova community panel */}
        {!m && (
          <aside
            className={demoProductInputScope ? DEMO_PRODUCT_INPUT_SCOPE_CLASS : undefined}
            style={{
              width: novaOpen ? 280 : 36,
              flexShrink: 0,
              transition: 'width 0.25s ease',
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              borderLeft: `0.5px solid ${hexWithAlpha(accent, 0.12)}`,
              background: 'var(--color-background-primary, #fff)',
              position: 'relative',
              overflow: 'visible',
            }}
          >
            <button
              type="button"
              onClick={() => setNovaOpen(!novaOpen)}
              style={{
                position: 'absolute',
                top: 14,
                left: novaOpen ? -14 : 8,
                width: 28,
                height: 28,
                borderRadius: '50%',
                background: 'var(--color-background-primary, #fff)',
                border: `0.5px solid ${hexWithAlpha(accent, 0.25)}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                zIndex: 10,
                boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                fontSize: 11,
                color: accent,
              }}
            >
              {novaOpen ? '›' : '‹'}
            </button>

            {novaOpen ? (
              <>
                {/* Panel top */}
                <div
                  style={{
                    background: `linear-gradient(140deg, ${accentBg}, ${introGradientEnd})`,
                    padding: '13px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 9,
                    borderBottom: `0.5px solid ${hexWithAlpha(accent, 0.1)}`,
                    flexShrink: 0,
                  }}
                >
                  <NovaAvatarImg size={32} alt="" chapter={novaAvatarChapter} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, color: '#2d1a22' }}>Nova</div>
                    <div style={{ fontSize: 10, color: accentText }}>{novaCommunityTitle}</div>
                  </div>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#1D9E75', marginLeft: 'auto', flexShrink: 0 }} />
                </div>

                {/* Panel body — First Chapter vs fertility */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '14px 15px' }}>
                  {isFirstChapter ? (
                    <>
                      <div style={{ background: accentBg, border: `0.5px solid ${hexWithAlpha(accent, 0.15)}`, borderRadius: 10, padding: '12px 13px', marginBottom: 14 }}>
                        <div style={{ fontSize: 11, color: '#2d1a22', lineHeight: 1.65, marginBottom: 7 }}>
                          9 people are in <span style={{ fontWeight: 500 }}>Cycle tracking beginners</span> right now — cramps, apps, and &quot;is this normal?&quot;
                        </div>
                        <span onClick={() => navigate('/live/live-fc-tracking')} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && navigate('/live/live-fc-tracking')} style={linkInline}>Join the room →</span>
                      </div>

                      <div style={{ height: '0.5px', background: 'var(--color-border-tertiary)', margin: '14px 0' }} />
                      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--color-text-tertiary)', marginBottom: 10 }}>Groups for you</div>
                      {FC_NOVA_GROUPS.map((g, i) => (
                        <div key={g.name} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '7px 0', borderBottom: i < FC_NOVA_GROUPS.length - 1 ? '0.5px solid var(--color-border-tertiary)' : 'none' }}>
                          <div style={{ width: 28, height: 28, borderRadius: 7, background: g.bg, flexShrink: 0 }} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: '#2d1a22' }}>{g.name}</div>
                            <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)' }}>{g.meta}</div>
                          </div>
                          {g.joined ? <span style={{ fontSize: 10, fontWeight: 500, color: '#0F6E56', background: '#E1F5EE', padding: '2px 8px', borderRadius: 999 }}>Joined</span> : <span onClick={() => navigate('/signup')} role="button" tabIndex={0} style={linkInline}>Join →</span>}
                        </div>
                      ))}

                      <div style={{ height: '0.5px', background: 'var(--color-border-tertiary)', margin: '14px 0' }} />
                      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--color-text-tertiary)', marginBottom: 10 }}>Friends online now</div>
                      {FC_FRIENDS_ONLINE.map((f, i) => (
                        <div key={f.name} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '7px 0', borderBottom: i < FC_FRIENDS_ONLINE.length - 1 ? '0.5px solid var(--color-border-tertiary)' : 'none' }}>
                          <div style={{ position: 'relative', flexShrink: 0 }}>
                            <div style={{ width: 30, height: 30, borderRadius: '50%', background: f.bg, color: f.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 500 }}>{initials(f.name)}</div>
                            <div style={{ position: 'absolute', bottom: 0, right: 0, width: 9, height: 9, borderRadius: '50%', background: '#1D9E75', border: '1.5px solid white' }} />
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <Link to={`${profileBasePath}/${f.slug}`} style={{ fontSize: 11, fontWeight: 700, color: accent, textDecoration: 'none' }}>{f.name}</Link>
                            <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)' }}>{f.desc}</div>
                          </div>
                          <span onClick={() => navigate(connectPath)} role="button" tabIndex={0} style={linkMessage}>Message</span>
                        </div>
                      ))}

                      <div style={{ height: '0.5px', background: 'var(--color-border-tertiary)', margin: '14px 0' }} />
                      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--color-text-tertiary)', marginBottom: 10 }}>People Nova recommends</div>
                      {FC_FRIENDS_RECOMMEND.map((f, i) => (
                        <div key={f.name} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '7px 0', borderBottom: i < FC_FRIENDS_RECOMMEND.length - 1 ? '0.5px solid var(--color-border-tertiary)' : 'none' }}>
                          <div style={{ width: 30, height: 30, borderRadius: '50%', background: f.bg, color: f.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 500, flexShrink: 0 }}>{initials(f.name)}</div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <Link to={`${profileBasePath}/${f.slug}`} style={{ fontSize: 11, fontWeight: 700, color: accent, textDecoration: 'none' }}>{f.name}</Link>
                            <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)' }}>{f.desc}</div>
                          </div>
                          <span onClick={() => navigate(connectPath)} role="button" tabIndex={0} style={linkInline}>Connect →</span>
                        </div>
                      ))}

                      <div style={{ height: '0.5px', background: 'var(--color-border-tertiary)', margin: '14px 0' }} />
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--color-text-tertiary)' }}>Live right now</div>
                        <span onClick={() => setActiveTab('live')} role="button" tabIndex={0} style={linkInline}>See all →</span>
                      </div>
                      {FC_LIVE_NOW.map((r, i) => (
                        <div key={r.name} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 0', borderBottom: i < FC_LIVE_NOW.length - 1 ? '0.5px solid var(--color-border-tertiary)' : 'none' }}>
                          <div style={{ width: 8, height: 8, borderRadius: '50%', background: r.orange ? '#D85A30' : '#1D9E75', flexShrink: 0 }} />
                          <div style={{ fontSize: 13, fontWeight: 600, color: '#2d1a22', flex: 1 }}>{r.name}</div>
                          <div style={{ fontSize: 10, color: r.orange ? '#D85A30' : '#1D9E75' }}>{r.count}</div>
                        </div>
                      ))}
                    </>
                  ) : (
                    <>
                      <div style={{ background: accentBg, border: `0.5px solid ${hexWithAlpha(accent, 0.15)}`, borderRadius: 10, padding: '12px 13px', marginBottom: 14 }}>
                        <div style={{ fontSize: 11, color: '#2d1a22', lineHeight: 1.65, marginBottom: 7 }}>
                          You're on stim day 8 — 7 women in <span style={{ fontWeight: 500 }}>Stims Support</span> are talking about monitoring results right now.
                        </div>
                        <span onClick={() => navigate('/live/stims-support')} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && navigate('/live/stims-support')} style={linkInline}>Join the room →</span>
                      </div>

                      <div style={{ height: '0.5px', background: 'var(--color-border-tertiary)', margin: '14px 0' }} />
                      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--color-text-tertiary)', marginBottom: 10 }}>Groups for you</div>
                      {[
                        { name: 'IVF Warriors — Cycle 1', meta: '3,240 members · your cycle', joined: true, bg: '#FBEAF0' },
                        { name: 'Low AMH Warriors', meta: '2,100 · Nova matched you', joined: false, bg: '#EEEDFE' },
                        { name: 'Fertility & Mental Health', meta: '1,890 members', joined: false, bg: '#FEF0EB' },
                        { name: 'PCOS Sisters', meta: '4,100 · Nova matched you', joined: false, bg: '#EEEDFE' },
                      ].map((g, i) => (
                        <div key={g.name} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '7px 0', borderBottom: i < 3 ? '0.5px solid var(--color-border-tertiary)' : 'none' }}>
                          <div style={{ width: 28, height: 28, borderRadius: 7, background: g.bg, flexShrink: 0 }} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: '#2d1a22' }}>{g.name}</div>
                            <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)' }}>{g.meta}</div>
                          </div>
                          {g.joined ? <span style={{ fontSize: 10, fontWeight: 500, color: '#0F6E56', background: '#E1F5EE', padding: '2px 8px', borderRadius: 999 }}>Joined</span> : <span onClick={() => navigate('/signup')} role="button" tabIndex={0} style={linkInline}>Join →</span>}
                        </div>
                      ))}

                      <div style={{ height: '0.5px', background: 'var(--color-border-tertiary)', margin: '14px 0' }} />
                      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--color-text-tertiary)', marginBottom: 10 }}>Friends online now</div>
                      {FRIENDS_ONLINE.map((f, i) => (
                        <div key={f.name} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '7px 0', borderBottom: i < FRIENDS_ONLINE.length - 1 ? '0.5px solid var(--color-border-tertiary)' : 'none' }}>
                          <div style={{ position: 'relative', flexShrink: 0 }}>
                            <div style={{ width: 30, height: 30, borderRadius: '50%', background: f.bg, color: f.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 500 }}>{initials(f.name)}</div>
                            <div style={{ position: 'absolute', bottom: 0, right: 0, width: 9, height: 9, borderRadius: '50%', background: '#1D9E75', border: '1.5px solid white' }} />
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <Link to={`${profileBasePath}/${f.slug}`} style={{ fontSize: 11, fontWeight: 700, color: accent, textDecoration: 'none' }}>{f.name}</Link>
                            <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)' }}>{f.desc}</div>
                          </div>
                          <span onClick={() => navigate(connectPath)} role="button" tabIndex={0} style={linkMessage}>Message</span>
                        </div>
                      ))}

                      <div style={{ height: '0.5px', background: 'var(--color-border-tertiary)', margin: '14px 0' }} />
                      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--color-text-tertiary)', marginBottom: 10 }}>Women Nova recommends</div>
                      {FRIENDS_RECOMMEND.map((f, i) => (
                        <div key={f.name} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '7px 0', borderBottom: i < FRIENDS_RECOMMEND.length - 1 ? '0.5px solid var(--color-border-tertiary)' : 'none' }}>
                          <div style={{ width: 30, height: 30, borderRadius: '50%', background: f.bg, color: f.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 500, flexShrink: 0 }}>{initials(f.name)}</div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <Link to={`${profileBasePath}/${f.slug}`} style={{ fontSize: 11, fontWeight: 700, color: accent, textDecoration: 'none' }}>{f.name}</Link>
                            <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)' }}>{f.desc}</div>
                          </div>
                          <span onClick={() => navigate(connectPath)} role="button" tabIndex={0} style={linkInline}>Connect →</span>
                        </div>
                      ))}

                      <div style={{ height: '0.5px', background: 'var(--color-border-tertiary)', margin: '14px 0' }} />
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--color-text-tertiary)' }}>Live right now</div>
                        <span onClick={() => setActiveTab('live')} role="button" tabIndex={0} style={linkInline}>See all →</span>
                      </div>
                      {LIVE_NOW.map((r, i) => (
                        <div key={r.name} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 0', borderBottom: i < LIVE_NOW.length - 1 ? '0.5px solid var(--color-border-tertiary)' : 'none' }}>
                          <div style={{ width: 8, height: 8, borderRadius: '50%', background: r.orange ? '#D85A30' : '#1D9E75', flexShrink: 0 }} />
                          <div style={{ fontSize: 13, fontWeight: 600, color: '#2d1a22', flex: 1 }}>{r.name}</div>
                          <div style={{ fontSize: 10, color: r.orange ? '#D85A30' : '#1D9E75' }}>{r.count}</div>
                        </div>
                      ))}
                    </>
                  )}
                </div>

                {/* Panel bottom */}
                <div style={{ borderTop: '0.5px solid var(--color-border-tertiary)', padding: '12px 15px', background: 'var(--color-background-primary)', flexShrink: 0 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--color-text-tertiary)', marginBottom: 8 }}>Ask Nova about community</div>
                  {(isFirstChapter ? FC_NOVA_ASK_CHIPS : ["Who's doing retrieval this week?", 'Which group is best for low AMH?', 'Find women near me in IVF']).map((q) => (
                    <div key={q} onClick={() => navigate('/nova')} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && navigate('/nova')} style={{ background: accentBg, color: accent, border: `0.5px solid ${hexWithAlpha(accent, 0.2)}`, borderRadius: 8, padding: '6px 9px', fontSize: 10, lineHeight: 1.4, cursor: 'pointer', marginBottom: 5 }}>{q}</div>
                  ))}
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 7 }}>
                    <input
                      type="text"
                      placeholder="Ask anything..."
                      onFocus={() => setAskInputFocused(true)}
                      onBlur={() => setAskInputFocused(false)}
                      style={{
                        flex: 1,
                        fontSize: 11,
                        background: 'var(--color-background-secondary)',
                        border: `0.5px solid ${askInputFocused ? accent : 'var(--color-border-secondary)'}`,
                        borderRadius: 8,
                        padding: '7px 10px',
                        color: '#2d1a22',
                        outline: 'none',
                        fontFamily: FONT_BODY,
                      }}
                    />
                    <button type="button" onClick={() => navigate('/nova')} style={{ width: 26, height: 26, borderRadius: '50%', background: accent, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M1 5h8M6 2l3 3-3 3" stroke="white" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 8 }}>
                <NovaAvatarImg size={28} alt="" chapter={novaAvatarChapter} style={{ marginBottom: 8 }} />
                <div style={{ fontSize: 10, color: accent, transform: 'rotate(-90deg)', whiteSpace: 'nowrap' }}>Community guide</div>
              </div>
            )}
          </aside>
        )}
      </div>
    </div>
    {m && navOpen && <MobileNavDrawer onClose={() => setNavOpen(false)} />}
    </>
  );
}

function FeedTab({
  navigate,
  m,
  feedSort,
  setFeedSort,
  isDemo,
  accent,
  accentBg,
  chapterKey,
  chapterMeta,
  demoChapterRaw,
}) {
  const isFirstChapterFeed = chapterKey === 'cycle' || chapterKey === 'first-chapter';
  const posts = isFirstChapterFeed
    ? FIRST_CHAPTER_FEED_POSTS
    : [
        { id: 'sarah', ...SARAH_THREAD.post, hasNovaThread: true, threadData: SARAH_THREAD },
        { id: 'clinical', ...CLINICAL_NOTE_THREAD.post, hasNovaThread: true, threadData: CLINICAL_NOTE_THREAD },
        { id: 'breath', ...TAKING_A_BREATH_THREAD.post, hasNovaThread: true, threadData: TAKING_A_BREATH_THREAD },
        { name: 'Maya', authorSlug: 'maya', initials: 'M', avatarBg: '#FBEAF0', avatarColor: '#993556', badge: 'friend', badgeStyle: { bg: '#E1F5EE', color: '#0F6E56', border: 'rgba(29,158,117,0.2)' }, date: '3/22/2026', text: 'Anyone else balancing sleep recovery and pumping? Looking for what helped you feel human again.', hearts: 12, sameBoat: 3, replies: 2 },
        { name: 'Rina', authorSlug: 'rina', initials: 'R', avatarBg: '#E6F1FB', avatarColor: '#185FA5', badge: 'community', badgeStyle: { bg: 'var(--color-background-secondary)', color: 'var(--color-text-tertiary)', border: 'var(--color-border-secondary)' }, date: '3/22/2026', text: 'Trigger shot tonight. Sharing my checklist in case it helps anyone else feeling anxious.', hearts: 8, sameBoat: 5, replies: 1 },
        {
          novaInsert: true,
          chapter: chapterKey,
          insertType: 'resource',
          tag: 'MENTAL HEALTH',
          headline: 'Coping with the two-week wait',
          body: "The TWW is one of the hardest stretches in this process. Here's what's helped others — and what to expect while you wait.",
          cta: { label: 'Read more →' },
          date: '3/22/2026',
          hearts: 24,
          replies: 6,
        },
        { name: 'Priya', authorSlug: 'priya', initials: 'P', avatarBg: '#EEEDFE', avatarColor: '#534AB7', badge: 'community', badgeStyle: { bg: 'var(--color-background-secondary)', color: 'var(--color-text-tertiary)', border: 'var(--color-border-secondary)' }, date: '3/22/2026', text: 'Just got my fertilization report — 8 out of 11 fertilized. Cautiously optimistic 💕', hearts: 31, sameBoat: 2, replies: 4 },
      ];

  return (
    <div style={demoSectionCardStackStyle}>
      <div style={{ background: accentBg, border: `0.5px solid ${hexWithAlpha(accent, 0.2)}`, borderRadius: 12, padding: '16px 18px' }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12 }}>
          <div style={{ width: 42, height: 42, borderRadius: '50%', background: accent, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 500, flexShrink: 0 }}>M</div>
          <div style={{ flex: 1, background: 'rgba(255,255,255,0.8)', border: `0.5px solid ${hexWithAlpha(accent, 0.15)}`, borderRadius: 10, padding: '11px 16px', fontSize: 13, color: 'rgba(60,30,45,0.38)', cursor: 'text' }}>Share something with your chapter...</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" style={{ ...btnGhost, background: 'var(--white)' }}>📷 Photo</button>
          <button type="button" style={{ ...btnGhost, background: 'var(--white)' }}>🎥 Video</button>
          <button type="button" style={{ ...btnGhost, background: 'var(--white)' }}>GIF</button>
          <span style={{ marginLeft: 'auto', background: 'rgba(255,255,255,0.9)', color: accent, border: `0.5px solid ${hexWithAlpha(accent, 0.3)}`, borderRadius: 999, padding: '2px 8px', fontSize: 10, fontWeight: 500 }}>{chapterMeta.emoji} {chapterMeta.label}</span>
          <button type="button" onClick={() => navigate('/signup')} style={{ ...btnPrimary, background: accent }}>Post</button>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ fontSize: 13, fontWeight: 500, color: '#2d1a22' }}>What's happening in your chapter</div>
        <div style={{ display: 'flex', gap: 12 }}>
          <span onClick={() => setFeedSort('recent')} role="button" tabIndex={0} style={{ fontSize: 11, cursor: 'pointer', color: feedSort === 'recent' ? accent : 'var(--color-text-tertiary)', borderBottom: feedSort === 'recent' ? `1px solid ${accent}` : 'none' }}>Recent</span>
          <span onClick={() => setFeedSort('top')} role="button" tabIndex={0} style={{ fontSize: 11, cursor: 'pointer', color: feedSort === 'top' ? accent : 'var(--color-text-tertiary)', borderBottom: feedSort === 'top' ? `1px solid ${accent}` : 'none' }}>Top</span>
        </div>
      </div>

      <CommunityFeed
        chapter={chapterKey}
        items={posts}
        onOpenNova={() => navigate('/nova')}
        profileBasePath={isDemo ? `/demo/${demoChapterRaw}/profile` : '/profile'}
      />
    </div>
  );
}

export function LiveTab({ navigate, chapterKey }) {
  const rooms = useMemo(() => {
    return LIVE_CHAT_ROOMS.filter((r) => {
      const ch = r.chapters || [];
      if (ch.includes(chapterKey)) return true;
      if (chapterKey === 'cycle' && ch.includes('first-chapter')) return true;
      if (chapterKey === 'first-chapter' && ch.includes('cycle')) return true;
      return false;
    });
  }, [chapterKey]);

  return (
    <LiveRoomsPage
      chapter={chapterKey}
      rooms={rooms}
      onJoinRoom={() => {}}
      onOpenNova={() => navigate('/nova')}
    />
  );
}

export function GroupsTab({ navigate, chapterMeta, accent, accentBg, chapterKey }) {
  const isFC = chapterKey === 'cycle' || chapterKey === 'first-chapter';
  const yourGroups = isFC ? FC_YOUR_GROUPS : YOUR_GROUPS;
  const suggestedGroups = isFC ? FC_SUGGESTED_GROUPS : SUGGESTED_GROUPS;
  const gradEnd = isFC ? '#f0eefc' : '#f5f0f5';

  return (
    <div style={demoSectionCardStackStyle}>
      <div
        style={{
          background: `linear-gradient(140deg, ${accentBg}, ${gradEnd})`,
          ...introCardKeyline,
          padding: '22px 26px',
        }}
      >
        <div style={{ fontSize: 16, fontWeight: 500, color: '#2d1a22', marginBottom: 4 }}>Groups & forums</div>
        <div style={{ fontSize: 12, color: 'rgba(60,30,45,0.5)' }}>Smaller communities within the {chapterMeta.label} chapter</div>
      </div>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--color-text-tertiary)', marginBottom: 10 }}>Your groups</div>
      <div style={{ background: 'var(--white)', border: `0.5px solid ${hexWithAlpha(accent, 0.1)}`, borderRadius: 12, overflow: 'hidden' }}>
        {yourGroups.map((g, i) => (
            <div key={g.name} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px', borderBottom: i < yourGroups.length - 1 ? `0.5px solid ${hexWithAlpha(accent, 0.07)}` : 'none' }}>
            <div style={{ width: 42, height: 42, borderRadius: 10, background: g.bg, flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: '#2d1a22' }}>{g.name}</div>
              <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>{g.members} members</div>
            </div>
            <span style={{ fontSize: 10, fontWeight: 500, color: '#0F6E56', background: '#E1F5EE', padding: '4px 10px', borderRadius: 999 }}>Joined</span>
          </div>
        ))}
      </div>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--color-text-tertiary)', marginBottom: 10 }}>Suggested for you</div>
      <div style={{ background: 'var(--white)', border: `0.5px solid ${hexWithAlpha(accent, 0.1)}`, borderRadius: 12, overflow: 'hidden' }}>
        {suggestedGroups.map((g, i) => (
          <div key={g.name} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px', borderBottom: i < suggestedGroups.length - 1 ? `0.5px solid ${hexWithAlpha(accent, 0.07)}` : 'none' }}>
            <div style={{ width: 42, height: 42, borderRadius: 10, background: g.bg, flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: '#2d1a22' }}>{g.name}</div>
              <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>{g.members} members{g.matched ? ' · Nova matched you' : ''}</div>
            </div>
            <button type="button" onClick={() => navigate('/signup')} style={btnGhost}>Join</button>
          </div>
        ))}
      </div>
    </div>
  );
}

export function FriendsTab({ navigate, profileBasePath, connectPath, accent, chapterKey }) {
  const isFC = chapterKey === 'cycle' || chapterKey === 'first-chapter';
  const friendsOnline = isFC ? FC_FRIENDS_ONLINE : FRIENDS_ONLINE;
  const friendsRecommend = isFC ? FC_FRIENDS_RECOMMEND : FRIENDS_RECOMMEND;
  const friendsIntroBg = isFC ? `linear-gradient(140deg, #EEEDFE, #E1F5EE)` : 'linear-gradient(140deg, rgb(251,234,240), rgb(225,245,238))';

  return (
    <div style={demoSectionCardStackStyle}>
      <div style={{ background: friendsIntroBg, ...introCardKeyline, padding: '22px 26px' }}>
        <div style={{ fontSize: 16, fontWeight: 500, color: '#2d1a22', marginBottom: 4 }}>Your connections</div>
        <div style={{ fontSize: 12, color: 'rgba(60,30,45,0.5)' }}>{isFC ? 'People Nova matched you with — same age-ish chapter, similar stuff' : 'Women Nova matched you with — same stage, similar story'}</div>
      </div>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--color-text-tertiary)', marginBottom: 10 }}>Online now</div>
      <div style={{ background: 'var(--white)', border: `0.5px solid ${hexWithAlpha(accent, 0.1)}`, borderRadius: 12, overflow: 'hidden' }}>
        {friendsOnline.map((f, i) => (
          <div key={f.name} style={{ display: 'flex', alignItems: 'center', gap: 13, padding: '13px 18px', borderBottom: i < friendsOnline.length - 1 ? `0.5px solid ${hexWithAlpha(accent, 0.07)}` : 'none' }}>
            <div style={{ position: 'relative', flexShrink: 0 }}>
              <div style={{ width: 42, height: 42, borderRadius: '50%', background: f.bg, color: f.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 500 }}>{initials(f.name)}</div>
              <div style={{ position: 'absolute', bottom: 0, right: 0, width: 11, height: 11, borderRadius: '50%', background: '#1D9E75', border: '2px solid white' }} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <Link to={`${profileBasePath}/${f.slug}`} style={{ fontSize: 13, fontWeight: 700, color: accent, textDecoration: 'none' }}>{f.name}</Link>
              <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>{f.desc}</div>
            </div>
            <button type="button" onClick={() => navigate(connectPath)} style={btnPrimarySmall}>Message</button>
          </div>
        ))}
      </div>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--color-text-tertiary)', marginBottom: 10 }}>{isFC ? 'People Nova recommends' : 'Women Nova recommends'}</div>
      <div style={{ background: 'var(--white)', border: `0.5px solid ${hexWithAlpha(accent, 0.1)}`, borderRadius: 12, overflow: 'hidden' }}>
        {friendsRecommend.map((f, i) => (
          <div key={f.name} style={{ display: 'flex', alignItems: 'center', gap: 13, padding: '13px 18px', borderBottom: i < friendsRecommend.length - 1 ? `0.5px solid ${hexWithAlpha(accent, 0.07)}` : 'none' }}>
            <div style={{ width: 42, height: 42, borderRadius: '50%', background: f.bg, color: f.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 500, flexShrink: 0 }}>{initials(f.name)}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <Link to={`${profileBasePath}/${f.slug}`} style={{ fontSize: 13, fontWeight: 700, color: accent, textDecoration: 'none' }}>{f.name}</Link>
              <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>{f.desc}</div>
            </div>
            <button type="button" onClick={() => navigate(connectPath)} style={btnGhost}>Connect</button>
          </div>
        ))}
      </div>
    </div>
  );
}

export function MyPostsTab({ navigate, profileBasePath, connectPath, accent }) {
  return (
    <div style={demoSectionCardStackStyle}>
      <div style={{ background: 'linear-gradient(140deg, rgb(251,234,240), rgb(225,245,238))', ...introCardKeyline, padding: '22px 26px', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 500, color: '#2d1a22', marginBottom: 4 }}>Your activity</div>
          <div style={{ fontSize: 12, color: 'rgba(60,30,45,0.5)' }}>1 post · 17 hearts · 8 replies · 3 friends</div>
        </div>
        <button type="button" onClick={() => navigate('/signup')} style={{ background: accent, color: 'white', border: 'none', borderRadius: 999, padding: '7px 18px', fontSize: 11, fontWeight: 500, cursor: 'pointer', flexShrink: 0 }}>+ New post</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: DEMO_MINI_CARD_GRID_GAP_PX }}>
          <div style={{ background: 'var(--color-background-secondary)', borderRadius: 10, padding: '14px 18px', textAlign: 'center' }}>
          <div style={{ fontSize: 20, fontWeight: 500, color: accent }}>1</div>
          <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 4 }}>Posts shared</div>
        </div>
        <div style={{ background: 'var(--color-background-secondary)', borderRadius: 10, padding: '14px 18px', textAlign: 'center' }}>
          <div style={{ fontSize: 20, fontWeight: 500, color: accent }}>17</div>
          <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 4 }}>Hearts received</div>
        </div>
      </div>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--color-text-tertiary)', marginBottom: 10 }}>Your posts</div>
      <div style={{ background: 'var(--white)', border: `0.5px solid ${hexWithAlpha(accent, 0.1)}`, borderRadius: 12, overflow: 'hidden' }}>
        <div style={{ padding: '16px 18px', display: 'flex', gap: 13, alignItems: 'flex-start' }}>
          <div style={{ width: 42, height: 42, borderRadius: '50%', background: '#FBEAF0', color: '#993556', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 500, flexShrink: 0 }}>M</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
              <span style={{ fontSize: 13, fontWeight: 500, color: '#2d1a22' }}>Maya</span>
              <span><span onClick={() => navigate('/signup')} role="button" tabIndex={0} style={linkInline}>Edit</span><span style={{ fontSize: 10, color: 'var(--color-border-tertiary)', padding: '0 4px' }}>·</span><span onClick={() => navigate('/signup')} role="button" tabIndex={0} style={linkInlineMuted}>Delete</span></span>
            </div>
            <div style={{ fontSize: 13, color: '#2d1a22', lineHeight: 1.5, marginBottom: 8 }}>Anyone else balancing sleep recovery and pumping? Looking for what helped you feel human again.</div>
            <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>3/22/2026 · ❤️ 12 · 🤝 3 · 💬 2</div>
          </div>
        </div>
      </div>
    </div>
  );
}
